// KnowledgeBase Quasar-style Code Blocks plugin
// Enhances code blocks in Preview with a Quasar-like header and copy button,
// without mounting Vue components.

import { registerMarkdownPostProcessor } from '../../services/PluginAPI.js'
import './styles/code.css'

function buildHeader(langLabel, codeText) {
  const header = document.createElement('div')
  header.className = 'kb-qcode-header'

  const title = document.createElement('div')
  title.className = 'kb-qcode-title'
  title.textContent = langLabel || ''

  const actions = document.createElement('div')
  actions.className = 'kb-qcode-actions'

  const copy = document.createElement('button')
  copy.className = 'kb-qcode-copy'
  copy.type = 'button'
  copy.title = 'Copy'
  copy.textContent = 'Copy'
  copy.addEventListener('click', async () => {
    try {
      await navigator.clipboard.writeText(codeText)
      copy.classList.add('copied')
      copy.textContent = 'Copied'
      setTimeout(() => {
        copy.classList.remove('copied')
        copy.textContent = 'Copy'
      }, 1200)
    } catch {
      /* noop */
    }
  })

  actions.appendChild(copy)
  header.appendChild(title)
  header.appendChild(actions)
  return header
}

function extractLangFromClass(codeEl) {
  const cls = (codeEl.getAttribute('class') || '').split(/\s+/)
  for (const c of cls) {
    if (c.startsWith('language-')) return c.slice(9)
    if (c.startsWith('lang-')) return c.slice(5)
  }
  return ''
}

function enhancePreCode(pre, code) {
  if (!pre || !code) return
  if (pre.__kb_qcode_applied) return
  pre.__kb_qcode_applied = true

  const container = document.createElement('div')
  container.className = 'kb-qcode'

  const lang = extractLangFromClass(code)
  const header = buildHeader(lang, code.textContent || '')

  const parent = pre.parentElement
  if (!parent) return

  parent.replaceChild(container, pre)
  container.appendChild(header)
  container.appendChild(pre)
}

function enhanceKbCodeblock(container) {
  if (!container || !container.classList || container.__kb_qcode_applied) return
  container.__kb_qcode_applied = true

  // container is .kb-codeblock[.kb-codeblock-lang]
  const langClass = Array.from(container.classList).find((c) => c.startsWith('kb-codeblock-'))
  const lang = langClass ? langClass.replace('kb-codeblock-', '') : ''
  const codeText = container.textContent || ''

  const header = buildHeader(lang, codeText)
  container.insertBefore(header, container.firstChild)
}

export function registerQuasarCodePlugin() {
  registerMarkdownPostProcessor(
    (root) => {
      // Enhance containers produced by code block processors first
      const kbs = Array.from(root.querySelectorAll('.kb-codeblock'))
      for (const el of kbs) {
        try {
          // Skip known interactive widgets that should not get a copy header
          if (
            el.classList.contains('kb-codeblock-dice') ||
            el.classList.contains('kb-codeblock-rtable') ||
            el.classList.contains('kb-rtable') ||
            // Skip iframe embeds; they are not code and may render their own UI
            el.classList.contains('kb-codeblock-iframe')
          ) {
            continue
          }
          enhanceKbCodeblock(el)
        } catch {}
      }

      // Enhance remaining standard pre>code blocks
      const codes = Array.from(root.querySelectorAll('pre > code'))
      for (const code of codes) {
        try {
          const pre = code.parentElement
          // Skip if already inside a kb-qcode
          if (pre && pre.parentElement && pre.parentElement.classList.contains('kb-qcode')) continue
          // Skip languages handled by custom processors (dice, rtable, random-table)
          const lang = extractLangFromClass(code)
          if (lang === 'dice' || lang === 'rtable' || lang === 'random-table' || lang === 'iframe')
            continue
          enhancePreCode(pre, code)
        } catch {}
      }
    },
    { sortOrder: 33 }
  )
}

export default { registerQuasarCodePlugin }
